import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { Heart, X, Star, RotateCcw, MessageCircle, MapPin, Loader2, UserPlus, Users2, Filter, Sparkles, Video, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const SWIPE_THRESHOLD = 120;

export default function Dating() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("swipe");                          // swipe | matches | filters
  const [me, setMe] = useState(null);
  const [feed, setFeed] = useState([]);
  const [feedIdx, setFeedIdx] = useState(0);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchModal, setMatchModal] = useState(null);               // {name, photo, conv_id}
  const [lastAction, setLastAction] = useState(null);               // for undo hint
  const [filters, setFilters] = useState({ min_age: 18, max_age: 55, max_km: 100, gender: "" });
  const [filtersTouched, setFiltersTouched] = useState(false);

  // Only pass filters to the API once the user has actually opened the panel and hit Apply —
  // otherwise every fresh signup sees an artificially small feed (age 18-55 + 100 km).
  const feedParams = useMemo(() => (filtersTouched ? filters : {}), [filtersTouched, filters]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const [mr, fr, mtr] = await Promise.all([
          api.get("/dating/me"),
          api.get("/dating/feed", { params: feedParams }),
          api.get("/dating/matches"),
        ]);
        setMe(mr.data);
        setFeed(fr.data.items || []);
        setFeedIdx(0);
        setMatches(mtr.data.items || []);
      } catch (e) {
        toast.error("Failed to load dating feed");
      } finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const reloadFeed = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dating/feed", { params: feedParams });
      setFeed(r.data.items || []); setFeedIdx(0);
    } finally { setLoading(false); }
  };

  const submitSwipe = async (target_listing_id, action) => {
    setLastAction({ target_listing_id, action });                    // for undo affordance (visual only)
    try {
      const r = await api.post("/dating/swipe", { target_listing_id, action });
      if (r.data.matched) {
        const target = feed.find(f => f.listing_id === target_listing_id);
        setMatchModal({
          name: r.data.target_name || target?.title || "Match",
          photo: r.data.target_photo || target?.images?.[0],
          conv_id: r.data.conv_id,
        });
        // Refresh matches quietly so the count updates.
        api.get("/dating/matches").then(mr => setMatches(mr.data.items || [])).catch(() => {});
      }
    } catch {
      toast.error("Swipe failed — try again");
    }
  };

  const onCardDragEnd = (card, info) => {
    const { offset, velocity } = info;
    const swipedRight = offset.x > SWIPE_THRESHOLD || velocity.x > 500;
    const swipedLeft  = offset.x < -SWIPE_THRESHOLD || velocity.x < -500;
    const swipedUp    = offset.y < -SWIPE_THRESHOLD;
    if (swipedRight) { submitSwipe(card.listing_id, "like");  setFeedIdx(i => i + 1); }
    else if (swipedLeft)  { submitSwipe(card.listing_id, "pass");  setFeedIdx(i => i + 1); }
    else if (swipedUp)    { submitSwipe(card.listing_id, "super"); setFeedIdx(i => i + 1); }
  };

  const buttonSwipe = (action) => {
    const card = feed[feedIdx];
    if (!card) return;
    submitSwipe(card.listing_id, action);
    setFeedIdx(i => i + 1);
  };

  // Show the top 3 cards in the stack for that layered-card look.
  const visibleCards = useMemo(() => feed.slice(feedIdx, feedIdx + 3).reverse(), [feed, feedIdx]);

  if (!user) {
    return <div className="max-w-md mx-auto p-12 text-center text-stone-500">Sign in to explore matches near you.</div>;
  }

  return (
    <div
      className="min-h-[calc(100vh-4rem)] relative overflow-hidden"
      style={{
        background: "radial-gradient(1000px 500px at 20% -10%, rgba(236,72,153,0.28), transparent 60%), radial-gradient(900px 500px at 90% 10%, rgba(217,70,239,0.28), transparent 60%), linear-gradient(180deg, #1a0b1f 0%, #2a0d2e 60%, #1a0b1f 100%)",
      }}
      data-testid="dating-page"
    >
      <div className="max-w-md mx-auto px-4 pt-6 pb-32">
        {/* Header + tabs */}
        <div className="flex items-center justify-between text-white mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-black text-pink-300/80">Find2Near</div>
            <h1 className="heading text-3xl font-black">
              <Heart className="inline text-pink-400 fill-pink-400 -mt-1" size={28} /> Dating
            </h1>
          </div>
          <button
            onClick={() => setTab(t => t === "filters" ? "swipe" : "filters")}
            className="w-10 h-10 rounded-full bg-white/10 border border-white/20 grid place-items-center text-white/80 hover:bg-white/20 transition-colors"
            data-testid="dating-filters-btn"
            aria-label="Filters"
          >
            <Filter size={16} />
          </button>
        </div>

        <div className="flex gap-1 mb-4 bg-black/25 rounded-full p-1 backdrop-blur border border-white/10">
          {[
            { k: "swipe",   l: "Discover", n: feed.length - feedIdx },
            { k: "matches", l: "Matches",  n: matches.length },
          ].map(t => {
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`flex-1 rounded-full py-2 text-xs font-black transition-colors ${active ? "bg-white text-stone-900" : "text-white/70 hover:text-white"}`}
                data-testid={`dating-tab-${t.k}`}
              >
                {t.l} {t.n > 0 && <span className={`ml-1 rounded-full text-[10px] font-mono px-1.5 ${active ? "bg-pink-100 text-pink-700" : "bg-white/10 text-white/70"}`}>{t.n}</span>}
              </button>
            );
          })}
        </div>

        {/* No profile yet — nudge them to create one */}
        {me && !me.has_profile && (
          <div className="mb-4 rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-4 text-white" data-testid="dating-no-profile">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-pink-500/30 grid place-items-center shrink-0"><UserPlus size={18} className="text-pink-300" /></div>
              <div className="flex-1">
                <div className="font-bold">Create your dating profile first</div>
                <div className="text-xs text-white/70 mt-0.5">Others can only see you if you have a public profile with a verified photo.</div>
                <Link to="/post?category=dating" className="mt-2 inline-block text-xs font-black bg-white text-pink-700 rounded-full px-3 py-1.5">Create profile →</Link>
              </div>
            </div>
          </div>
        )}

        {/* Has profile but no video intro — subtle nudge */}
        {me?.has_profile && !me?.profile?.video_intro_url && (
          <div className="mb-4 rounded-2xl bg-fuchsia-500/15 backdrop-blur border border-fuchsia-300/30 p-3 text-white flex items-center gap-3" data-testid="dating-add-video">
            <div className="w-9 h-9 rounded-full bg-fuchsia-500/40 grid place-items-center shrink-0"><Video size={16} className="text-fuchsia-200"/></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">Add a 30 s video intro</div>
              <div className="text-[11px] text-white/70">Profiles with videos get 3× more matches.</div>
            </div>
            <Link to="/video-intros" className="text-xs font-black bg-white text-fuchsia-700 rounded-full px-3 py-1.5">Attach →</Link>
          </div>
        )}

        {/* Filters panel */}
        <AnimatePresence>
          {tab === "filters" && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="mb-4 rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-4 text-white space-y-3"
              data-testid="dating-filters-panel"
            >
              <div>
                <label className="text-[11px] uppercase tracking-widest font-black text-white/60">Age range</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="number" min={18} max={99} value={filters.min_age} onChange={e => setFilters(f => ({ ...f, min_age: +e.target.value }))} className="w-16 bg-black/30 rounded-lg px-2 py-1 text-center outline-none" data-testid="filter-min-age" />
                  <span>–</span>
                  <input type="number" min={18} max={99} value={filters.max_age} onChange={e => setFilters(f => ({ ...f, max_age: +e.target.value }))} className="w-16 bg-black/30 rounded-lg px-2 py-1 text-center outline-none" data-testid="filter-max-age" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-black text-white/60">Within (km)</label>
                <input type="range" min={5} max={500} value={filters.max_km} onChange={e => setFilters(f => ({ ...f, max_km: +e.target.value }))} className="w-full accent-pink-500" data-testid="filter-max-km" />
                <div className="text-xs text-white/60">{filters.max_km} km</div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-black text-white/60">Show me</label>
                <div className="flex gap-1 mt-1">
                  {[
                    { v: "",       l: "Everyone" },
                    { v: "female", l: "Women" },
                    { v: "male",   l: "Men" },
                    { v: "other",  l: "Non-binary" },
                  ].map(g => (
                    <button key={g.v} onClick={() => setFilters(f => ({ ...f, gender: g.v }))} className={`text-xs font-bold rounded-full px-3 py-1 border transition-colors ${filters.gender === g.v ? "bg-pink-500 text-white border-pink-500" : "bg-black/30 text-white/70 border-white/10 hover:border-white/30"}`}>
                      {g.l}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={() => { setFiltersTouched(true); reloadFeed(); setTab("swipe"); }} className="w-full rounded-full bg-pink-500 hover:bg-pink-600" data-testid="filters-apply">Apply filters</Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Discover tab — card stack */}
        {tab === "swipe" && (
          <>
            <div className="relative h-[500px] mb-4" data-testid="dating-stack">
              {loading ? (
                <div className="absolute inset-0 grid place-items-center text-white/60"><Loader2 className="animate-spin" /></div>
              ) : feedIdx >= feed.length ? (
                <EmptyDeck onReload={reloadFeed} matches={matches.length} onSeeMatches={() => setTab("matches")} />
              ) : (
                visibleCards.map((card, idx) => {
                  const positionInStack = visibleCards.length - 1 - idx;    // 0 = top
                  return (
                    <SwipeCard
                      key={card.listing_id}
                      card={card}
                      positionInStack={positionInStack}
                      isTop={positionInStack === 0}
                      onDragEnd={(info) => onCardDragEnd(card, info)}
                    />
                  );
                })
              )}
            </div>

            {/* Action bar */}
            {feedIdx < feed.length && !loading && (
              <div className="flex items-center justify-center gap-4" data-testid="dating-action-bar">
                <ActionBtn onClick={() => buttonSwipe("pass")}   Icon={X}     bg="bg-white text-red-500"    label="Pass"       testid="btn-pass" />
                <ActionBtn onClick={() => buttonSwipe("super")}  Icon={Star}  bg="bg-blue-400 text-white"   label="Super Like" size="sm" testid="btn-super" />
                <ActionBtn onClick={() => buttonSwipe("like")}   Icon={Heart} bg="bg-pink-500 text-white"   label="Like"       testid="btn-like" />
              </div>
            )}
          </>
        )}

        {/* Matches tab */}
        {tab === "matches" && (
          <div className="space-y-2" data-testid="dating-matches-list">
            {matches.length === 0 ? (
              <div className="text-center text-white/60 py-16">
                <Users2 size={40} className="mx-auto mb-2 opacity-50" />
                No matches yet — keep swiping.
              </div>
            ) : matches.map(m => (
              <MatchRow key={m.match_id} m={m} onOpen={() => navigate(`/messages#${m.conv_id}`)} />
            ))}
          </div>
        )}
      </div>

      {/* IT'S A MATCH modal */}
      <AnimatePresence>
        {matchModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md grid place-items-center px-6"
            onClick={() => setMatchModal(null)}
            data-testid="dating-match-modal"
          >
            <motion.div
              initial={{ scale: 0.6, rotate: -6 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0.6, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 18 }}
              className="text-center text-white max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <Sparkles className="mx-auto text-pink-300 mb-2" size={40} />
              <div className="heading text-5xl font-black bg-clip-text text-transparent bg-gradient-to-r from-pink-300 to-fuchsia-400 mb-1">It's a match!</div>
              <div className="text-white/70 mb-4">You and {matchModal.name} liked each other.</div>
              {matchModal.photo && (
                <img loading="lazy" src={matchModal.photo} alt="" className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-pink-400 shadow-2xl" />
              )}
              <div className="flex gap-2 mt-6">
                <Button variant="outline" onClick={() => setMatchModal(null)} className="flex-1 rounded-full text-black" data-testid="match-keep-swiping">Keep swiping</Button>
                <Button onClick={() => { setMatchModal(null); if (matchModal.conv_id) navigate(`/messages#${matchModal.conv_id}`); }} className="flex-1 rounded-full bg-pink-500 hover:bg-pink-600" data-testid="match-say-hi">
                  <MessageCircle size={14} className="mr-1.5" /> Say hi
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* -------------------- Building blocks -------------------- */

function SwipeCard({ card, positionInStack, isTop, onDragEnd }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-18, 18]);
  const likeOpacity = useTransform(x, [50, 150], [0, 1]);
  const passOpacity = useTransform(x, [-150, -50], [1, 0]);
  const superOpacity = useTransform(y, [-150, -50], [1, 0]);
  const [showVideo, setShowVideo] = useState(false);

  const photo = (card.images && card.images[0]) || null;
  const video = card.video_intro_url || null;
  const isYT  = video && /(youtube\.com|youtu\.be)/i.test(video);

  return (
    <motion.div
      className="absolute inset-0"
      style={{
        x: isTop ? x : 0,
        y: isTop ? y : 0,
        rotate: isTop ? rotate : 0,
        scale: 1 - positionInStack * 0.04,
        zIndex: 10 - positionInStack,
        top: positionInStack * 8,
      }}
      drag={isTop && !showVideo}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.9}
      onDragEnd={(_, info) => isTop && !showVideo && onDragEnd(info)}
      data-testid={`swipe-card-${card.listing_id}`}
    >
      <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl border border-white/20 bg-stone-900">
        {showVideo && video ? (
          <div className="absolute inset-0 bg-black">
            {isYT ? (
              <iframe title="Video intro" className="w-full h-full"
                src={video.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/").split("&")[0] + "?autoplay=1"}
                allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen/>
            ) : (
              <video src={video} controls autoPlay className="w-full h-full object-contain"/>
            )}
            <button onClick={(e) => { e.stopPropagation(); setShowVideo(false); }}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white grid place-items-center border border-white/20"
              data-testid={`swipe-card-video-close-${card.listing_id}`}
              aria-label="Close video">
              <X size={16}/>
            </button>
          </div>
        ) : (
          <>
            {photo ? (
              <img loading="lazy" src={photo} alt={card.title} className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none" draggable={false} />
            ) : (
              <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-pink-700 to-fuchsia-900 text-white/60">
                <Users2 size={40} />
              </div>
            )}
            {/* Bottom gradient overlay */}
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
            {/* LIKE / PASS / SUPER hint overlays */}
            <motion.div style={{ opacity: likeOpacity }} className="absolute top-8 right-6 border-4 border-pink-400 text-pink-400 font-black text-3xl px-4 py-1 rounded-lg rotate-12 pointer-events-none">LIKE</motion.div>
            <motion.div style={{ opacity: passOpacity }} className="absolute top-8 left-6 border-4 border-red-400 text-red-400 font-black text-3xl px-4 py-1 rounded-lg -rotate-12 pointer-events-none">NOPE</motion.div>
            <motion.div style={{ opacity: superOpacity }} className="absolute top-8 left-1/2 -translate-x-1/2 border-4 border-blue-400 text-blue-400 font-black text-3xl px-4 py-1 rounded-lg pointer-events-none">SUPER</motion.div>
            {video && (
              <button onClick={(e) => { e.stopPropagation(); setShowVideo(true); }}
                className="absolute top-4 left-4 inline-flex items-center gap-1 text-xs font-black bg-black/60 text-white rounded-full px-2.5 py-1 border border-white/20 backdrop-blur"
                data-testid={`swipe-card-video-open-${card.listing_id}`}>
                <PlayCircle size={12}/> Watch intro
              </button>
            )}
            {/* Card content */}
            <div className="absolute inset-x-0 bottom-0 p-5 text-white pointer-events-none">
              <div className="flex items-end gap-2">
                <h2 className="heading text-3xl font-black leading-tight truncate">{card.title || "Someone"}</h2>
                {card.age && <span className="text-2xl font-bold opacity-90">{card.age}</span>}
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-white/80">
                {card.city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {card.city}</span>}
                {typeof card.distance_km === "number" && <span>· {card.distance_km} km away</span>}
              </div>
              {(card.bio || card.description) && (
                <div className="mt-2 text-sm text-white/90 line-clamp-3">{card.bio || card.description}</div>
              )}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

function ActionBtn({ onClick, Icon, bg, label, testid, size = "md" }) {
  const dim = size === "sm" ? "w-12 h-12" : "w-16 h-16";
  const iconSz = size === "sm" ? 18 : 24;
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={`${dim} rounded-full ${bg} shadow-xl grid place-items-center hover:scale-110 active:scale-90 transition-transform`}
      data-testid={testid}
    >
      <Icon size={iconSz} className="drop-shadow" />
    </button>
  );
}

function EmptyDeck({ onReload, matches, onSeeMatches }) {
  return (
    <div className="absolute inset-0 grid place-items-center text-center text-white" data-testid="dating-empty">
      <div>
        <Heart size={44} className="mx-auto text-pink-400 mb-2 opacity-80" />
        <div className="heading text-2xl font-black mb-1">That's everyone for now.</div>
        <div className="text-white/70 text-sm mb-4">Come back in a while — new profiles are added every day.</div>
        <div className="flex gap-2 justify-center">
          <Button onClick={onReload} variant="outline" className="rounded-full text-black" data-testid="empty-reload">
            <RotateCcw size={14} className="mr-1.5" /> Reload
          </Button>
          {matches > 0 && (
            <Button onClick={onSeeMatches} className="rounded-full bg-pink-500 hover:bg-pink-600" data-testid="empty-see-matches">
              See {matches} match{matches === 1 ? "" : "es"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchRow({ m, onOpen }) {
  const p = m.profile || {};
  const photo = (p.images || [])[0];
  return (
    <button
      onClick={onOpen}
      className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/10 backdrop-blur border border-white/15 hover:bg-white/15 transition-colors text-left"
      data-testid={`match-row-${m.match_id}`}
    >
      <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 bg-pink-500/30 grid place-items-center border-2 border-pink-400/40">
        {photo ? <img loading="lazy" src={photo} alt="" className="w-full h-full object-cover" /> : <Heart size={18} className="text-pink-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-bold flex items-center gap-1.5 truncate">
          {p.title || "Match"}
          {m.super_like && <Star size={12} className="text-blue-300 fill-blue-300" />}
        </div>
        <div className="text-xs text-white/60 truncate">{p.city || ""} · Matched {new Date(m.matched_at).toLocaleDateString()}</div>
      </div>
      <MessageCircle size={16} className="text-pink-300 shrink-0" />
    </button>
  );
}
